import { Vue, Component, Watch } from 'vue-property-decorator';

import * as d3 from 'd3';
import * as math from 'mathjs';
import _ from 'lodash';
import Datum from './Datum';
import AreaGroupRenderer from './AreaGroupRenderer';
import { getImageUrl, getImageJson } from '@/firebase/firebaseStorage';
import { Dictionary } from 'vue-router/types/router';
import ColorPaletteContainer from '@/components/ColorPaletteContainer/ColorPaletteContainer.vue';
import { eventBus } from '@/utils/EventBus';

@Component({
  components: { ColorPaletteContainer }
})
export default class MultipleColorPalettes extends Vue {
  public showCutoffArea: boolean = false;
  public showComponentArea: boolean = false;

  private isForEnglishPaper: boolean = true; // English or 한글. user-test를 위한 레이아웃이 약간 변경될 수 있다.
  private showConcentricCurveGroup: boolean = false;

  private src: string = '';
  private data: Datum | undefined;

  private generationPolicy: 'number' | 'cutoff' = 'number';

  private forceNumberSliderDisabled = false;
  private forceCutoffSliderDisabled = false;

  private numComponents: number = 5;
  private minNumComponents: number = 3;
  private maxNumComponents: number = 8;
  private numComponentsTickLabels: number[] = math
    .range(this.minNumComponents, this.maxNumComponents, 1, true)
    .toArray() as number[];

  private wardCutoff: number = 100;
  private minWardCutoff = 2;
  private maxWardCutoff: number = 1000; // it will be updated later

  private isDepthChanged: boolean = false; // to prevent redundant re-drawing
  private isNumberChanged: boolean = false; // to prevent redundant re-drawing
  private isCutoffChanged: boolean = false; // to prevent redundant re-drawing

  private svg!: d3.Selection<SVGSVGElement, any, HTMLElement, unknown>;
  private linkGroup!: d3.Selection<SVGGElement, any, HTMLElement, unknown>;
  private nodeGroup!: d3.Selection<SVGGElement, any, HTMLElement, unknown>;
  private concentricCurveGroup!: d3.Selection<
    SVGGElement,
    any,
    HTMLElement,
    unknown
  >;
  private verticalLayout: 'topdown' | 'bottomup' = 'topdown';
  private horizontalLayout: 'linearTidy' | 'radialTidy' = 'radialTidy';

  private collapseAfter = 0;

  private clickedNode: d3.HierarchyPointNode<Datum> | undefined = undefined;

  private areaGroupRenderer: AreaGroupRenderer = new AreaGroupRenderer(this);

  private controllerShowed: boolean = true;
  private root!: d3.HierarchyPointNode<Datum>;
  private componentNodes: Array<d3.HierarchyPointNode<Datum>> = [];

  private readonly interactionLabelsDict = {
    english: {
      merge: 'Merge',
      split: 'Split',
      alter: 'Alter',
      revert: 'Revert'
    },
    korean: {
      merge: '병합',
      split: '분할',
      alter: '교체',
      revert: '복귀'
    }
  };
  private interactionRecords: Array<{
    sourceNode: d3.HierarchyPointNode<Datum>;
    targetNodes: Array<d3.HierarchyPointNode<Datum>>;
    interactionLabel: string | null;
  }> = [];
  private initialComponentNodes: Array<d3.HierarchyPointNode<Datum>> = []; // record용

  public isClickedNode(node: d3.HierarchyPointNode<Datum>) {
    return this.clickedNode === node;
  }

  public isComponent(node: d3.HierarchyPointNode<Datum>): boolean {
    return this.componentNodes.includes(node);
  }

  public hasComponent(node: d3.HierarchyPointNode<Datum>): boolean {
    return node.descendants().some(this.isComponent);
  }

  public numberSliderDisabled() {
    return this.forceNumberSliderDisabled;
  }

  public cutoffSliderDisabled() {
    return this.forceCutoffSliderDisabled;
  }

  @Watch('verticalLayout')
  @Watch('horizontalLayout')
  protected async onLayoutChanged() {
    this.updateLayout();
    await this.draw();
    this.updateViewBox();
  }

  protected async onCollapseAfterChanged() {
    this.applyDepthLimit();
    await this.onLayoutChanged();
  }

  /**
   * component 하위 depth를 조절하는 slider event listener
   * @param arg
   */
  protected async handleDepthSliderEvent(arg: number | Event) {
    if (Number.isInteger(arg as number)) {
      eventBus.$emit('collapseAfterChanged', arg);
    } else {
      switch ((arg as Event).type) {
        case 'click': // handle v-on:click
          // 멀티플로 보낼 수 있는 이벤트
          eventBus.$emit('collapseAfterChanged', arg);
          break;
      }
    }
  }

  protected async handleNumberSliderEvent(arg: number | Event) {
    if (Number.isInteger(arg as number)) { // handle v-on:input
      this.numComponents = arg as number;
      this.isNumberChanged = true;
    } else {
      switch ((arg as Event).type) {
        case 'mousedown': // handle v-on:mousedown
          this.forceCutoffSliderDisabled = true;
          break;
        case 'click': // handle v-on:click
          this.forceCutoffSliderDisabled = false;

          if (!this.isNumberChanged) { return; }

          // this.saveComponents();
          const conditionFn = nodes => nodes.length < this.numComponents;
          this.joinComponents(this.findComponents(this.root, this.getWard, conditionFn));
          this.wardCutoff = this.getWard(this.componentNodes.slice(-1)[0]);
          await this.onCollapseAfterChanged();

          this.isNumberChanged = false;

          break;
      }
    }
  }

  protected async clickNumberOfInitialComponentsRadio(value: number) {
    this.numComponents = value;
    const conditionFn = nodes => nodes.length < this.numComponents;
    this.joinComponents(
      this.findComponents(this.root, this.getWard, conditionFn)
    );
    this.wardCutoff = this.getWard(this.componentNodes.slice(-1)[0]);
    await this.onCollapseAfterChanged();
  }

  protected async handleCutoffSliderEvent(arg: Event | number) {
    if (Number.isFinite(arg as number)) {
      // handle v-on:input
      this.wardCutoff = arg as number;
      this.isCutoffChanged = true;
    } else {
      switch ((arg as Event).type) {
        case 'mousedown': // hande v-on:mousedown
          this.forceNumberSliderDisabled = true;
          break;
        case 'click': // hande v-on:click
          this.forceNumberSliderDisabled = false;

          if (!this.isCutoffChanged) {
            return;
          }

          const conditionFn = nodes =>
            this.getWard(nodes.slice(-1)[0]) >= this.wardCutoff;
          this.joinComponents(
            this.findComponents(this.root, this.getWard, conditionFn)
          );
          this.numComponents = this.componentNodes.length;
          await this.onCollapseAfterChanged();

          this.isCutoffChanged = false;

          break;
      }
    }
  }

  private updateLayout() {
    this.getLayout()(this.root);
  }

  private async updateViewBox(duration: number = 250) {
    await this.svg
      .transition()
      .duration(duration)
      .attr('viewBox', this.autoBox)
      .end();
  }

  private foldNode(node: d3.HierarchyPointNode<Datum>) {
    if (this.isUnfolded(node)) {
      node.data._children = node.children; // save children
      node.children = undefined; // collapse children
    } else {
      console.error(`Node-${node.data.id} is already folded:`, node);
    }
  }

  private unfoldNode(node: d3.HierarchyPointNode<Datum>) {
    if (this.isFolded(node)) {
      node.children = node.data._children;
      node.data._children = undefined;
    } else {
      console.error(`Node-${node.data.id} is already unfolded:`, node);
    }
  }

  private isFolded(node: d3.HierarchyPointNode<Datum>) {
    return !node.children && node.data._children;
  }

  private isUnfolded(node: d3.HierarchyPointNode<Datum>) {
    return !!node.children && !node.data._children;
  }

  private applyDepthLimit() {
    // Unfold any folded components
    this.componentNodes.filter(this.isFolded).forEach(this.unfoldNode);

    // Apply folding/unfolding
    this.componentNodes.forEach(depthPivot => {
      depthPivot.each(foldingPivot => {
        // The traversal starts from the note itself.
        if (depthPivot === foldingPivot) { return; } // skip

        const mustBeFolded = this.collapseAfter <= foldingPivot.depth - depthPivot.depth;

        if (mustBeFolded && this.isUnfolded(foldingPivot)) {
          foldingPivot.each(n => {
            // The traversal starts from the note itself.
            if (foldingPivot === n) { return; } // skip
            n.data.source = foldingPivot;
          });
          this.foldNode(foldingPivot);
        } else if (!mustBeFolded && this.isFolded(foldingPivot)) {
          this.unfoldNode(foldingPivot);
          foldingPivot.each(n => {
            // The traversal starts from the note itself.
            if (foldingPivot === n) { return; } // skip
            n.data.source = foldingPivot;
          });
        }

      });
    });

    // this.componentNodes.filter(this.isUnfolded).forEach(this.foldNode);

    // // Apply folding/unfolding
    // this.componentNodes.forEach(depthPivot => {
    //   depthPivot.each(foldingPivot => {
    //     // The traversal starts from the note itself.
    //     // if (depthPivot === foldingPivot) { return; } // skip

    //     const mustBeFolded = this.collapseAfter <= foldingPivot.depth - depthPivot.depth;

    //     if (mustBeFolded && this.isUnfolded(foldingPivot)) {
    //       foldingPivot.each(n => {
    //         // The traversal starts from the note itself.
    //         if (foldingPivot === n) { return; } // skip
    //         n.data.source = foldingPivot;
    //       });
    //       this.foldNode(foldingPivot);
    //     } else if (!mustBeFolded && this.isFolded(foldingPivot)) {
    //       this.unfoldNode(foldingPivot);
    //       foldingPivot.each(n => {
    //         // The traversal starts from the note itself.
    //         if (foldingPivot === n) { return; } // skip
    //         n.data.source = foldingPivot;
    //       });
    //     }

    //   });
    // });
  }

  private async mounted() {
    const { dirname, filename } = this.$route.query as Dictionary<string>;

    console.assert(!!dirname && !!filename);

    this.src = await getImageUrl(dirname + '/thumb', filename);
    this.data = await getImageJson<Datum>(
      dirname,
      filename.split('.')[0] + '.json'
    );

    this.svg = d3
      .select<SVGSVGElement, any>('svg#chart')
      .style('background-color', 'none')
      .style('width', '100%')
      .style('height', '100%')
      .style('font', '10px sans-serif')
      .style('margin', '5px')
      .style('user-select', 'none')
      .on('click', () => {
        if (this.clickedNode) {
          const parent = this.clickedNode.parent!;
          this.clickedNode = undefined;
          this.interactionRecords.pop();
          this.drawHierarchy(250, parent);
        }
      });

    this.linkGroup = d3
      .select<SVGGElement, any>('g#linkGroup')
      .attr('fill', 'none')
      .attr('stroke', '#555')
      .attr('stroke-opacity', 0.4)
      .attr('stroke-width', 1.5);

    this.nodeGroup = d3
      .select<SVGGElement, any>('g#nodeGroup')
      .attr('pointer-events', 'all');

    this.concentricCurveGroup = d3
      .select<SVGGElement, any>('g#concentricCurveGroup')
      .attr('fill', 'none')
      .attr('stroke', '#555')
      .attr('stroke-width', 1.0)
      .attr('stroke-dasharray', '5 5');

    const layout = this.getLayout();
    this.root = layout(d3.hierarchy(this.data));
    this.root.descendants().forEach((node, index) => {
      this.saveCurrentPosition(node);
    });

    // There will be 3 components when 'wardCutoff' reaches 'maxWardCutoff'.
    this.maxWardCutoff = Math.min(
      this.data.children![0].ward,
      this.data.children![1].ward
    );
    // this.wardCutoff = this.maxWardCutoff;

    this.root.eachBefore(node => {
      node.data.rgb = d3.lab.apply(null, node.data.lab).rgb();
    });

    const conditionFn = nodes => nodes.length < this.numComponents;
    this.componentNodes = this.findComponents(
      this.root,
      this.getWard,
      conditionFn
    );
    this.wardCutoff = this.getWard(this.componentNodes.slice(-1)[0]);

    window.addEventListener(
      'resize',
      _.debounce(async () => {
        try {
          this.updateLayout();
          await this.draw(125, this.root);
          await this.updateViewBox(125);
        } catch (e) {
          console.exception(e);
        }
      }, 250)
    );

    this.initialComponentNodes = _.cloneDeep(
      this.getComponentsSortedForDisplay()
    );

    /**
     * Initial Drawing
     */
    this.applyDepthLimit();
    this.updateLayout();
    await this.draw(0, this.root);
    this.updateViewBox();
  }

  private findComponents(
    root: d3.HierarchyPointNode<Datum>,
    weightFn: (node: d3.HierarchyPointNode<Datum>) => number,
    conditionFn: (nodes: Array<d3.HierarchyPointNode<Datum>>) => boolean
  ) {
    const compareFn = (
      a: d3.HierarchyPointNode<Datum>,
      b: d3.HierarchyPointNode<Datum>
    ) => weightFn(a) - weightFn(b);
    const cs = [root];
    while (conditionFn(cs) && cs.some(c => !!c.children)) {
      const last = cs.pop()!;
      const children = last.children;
      if (!!children) {
        cs.push(...children);
        cs.sort(compareFn); // the last one will be the heaviest one.
      } else {
        cs.unshift(last); // until the children is defined, those with no children go to the first.
      }
    }
    return cs.sort(compareFn); // the last one will be the heaviest one.
  }

  private async draw(
    duration: number = 500,
    source?: d3.HierarchyPointNode<Datum> | undefined
  ) {
    if (this.horizontalLayout === 'linearTidy') {
      // before the change of layout
      this.showConcentricCurveGroup = false;
    }

    await Promise.all([
      /**
       * Draw Hierarchy
       */
      this.drawHierarchy(duration, source),
      /**
       * Draw Hinting Area
       */
      this.drawHintingArea(),
      /**
       * Draw Concentric Circles
       */
      this.drawConcentricCircles()
    ]);

    if (this.horizontalLayout === 'radialTidy') {
      // after the change of layout
      this.showConcentricCurveGroup = true;
    }
  }

  private drawDepthLines() {
    const paths = Object.values<Array<d3.HierarchyPointNode<Datum>>>(
      this.root.descendants().reduce(
        (table, node) => {
          const key = node.depth;
          if (!table[key]) {
            table[key] = [];
          }
          table[key].push(node);
          return table;
        }, // reducer
        {} // empty table
      )
    ).map(
      (nodes, index) => {
        nodes.sort((a, b) => a.x - b.x); // sorted in place
        const points = nodes.map<[number, number]>(node => [node.x, node.y]);
        return { id: `${index}`, d: d3.line().curve(d3.curveLinear)(points) };
      } // mapper
    );
    this.concentricCurveGroup.selectAll('circle').remove();
    const depthLines = this.concentricCurveGroup
      .selectAll<
        SVGPathElement,
        {
          id: string;
          r: number;
        }
      >('path')
      .data(paths, d => d.id);

    const depthLinesEnter = depthLines
      .enter()
      .append('path')
      .attr('d', d => d.d)
      .attr('stroke-opacity', 0);
    const depthLinesUpdate = depthLines
      .merge(depthLinesEnter)
      .transition()
      .duration(500)
      .attr('d', d => d.d)
      .attr('stroke-opacity', 0.4);
    depthLines
      .exit()
      .transition()
      .duration(500)
      .attr('stroke-opacity', 0)
      .remove();
    return depthLinesUpdate.end();
  }

  private drawConcentricCircles(): Promise<void> {
    const radii = Object.values<Array<d3.HierarchyPointNode<Datum>>>(
      this.root.descendants().reduce(
        (table, node) => {
          const key = node.depth;
          if (!table[key]) {
            table[key] = [];
          }
          table[key].push(node);
          return table;
        }, // reducer
        {} // empty table
      )
    ).map(
      row => {
        const node = row.sort((a, b) => a.height - b.height).slice(-1)[0];
        return {
          id: `${node.depth}`,
          r: Math.sqrt(Math.pow(node.x, 2) + Math.pow(node.y, 2))
        };
      } // mapper
    );
    this.concentricCurveGroup.selectAll('path').remove();
    const concentricCircles = this.concentricCurveGroup
      .selectAll<
        SVGCircleElement,
        {
          id: string;
          r: number;
        }
      >('circle')
      .data(radii, d => d.id);
    const concentricCirclesEnter = concentricCircles
      .enter()
      .append('circle')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', d => d.r)
      .attr('stroke-opacity', 0);
    const concentricCirclesUpdate = concentricCircles
      .merge(concentricCirclesEnter)
      .transition()
      .duration(500)
      .attr('r', d => d.r)
      .attr('stroke-opacity', 0.4);
    concentricCircles
      .exit()
      .transition()
      .duration(500)
      .attr('stroke-opacity', 0)
      .remove();
    return concentricCirclesUpdate.end();
  }

  private async drawHierarchy(
    duration: number = 500,
    source?: d3.HierarchyPointNode<Datum> | undefined
  ) {
    const nodes = this.root.descendants().reverse();

    const links = this.root.links();

    const node = this.nodeGroup
      .selectAll<SVGGElement, d3.HierarchyPointNode<Datum>>('g')
      .data(nodes, d => `${d.data.id}`);

    const nodeEnter = node
      .enter()
      .append('g')
      .attr('transform', d => {
        const src = source ? source : d.data.source;
        const o = { x: src.data.x0!, y: src.data.y0! };
        return this.getNodeTransform(o);
      })
      .attr('stroke-linejoin', 'round')
      .attr('stroke-width', 4)
      .attr('fill-opacity', 0)
      .attr('stroke-opacity', 0)
      .attr('cursor', d =>
        this.isComponent(d) || this.isForEdit(d) ? 'pointer' : 'default'
      )
      .on('click', this.onNodeClicked);

    nodeEnter.append('circle').call(this.drawNodeCircle);

    nodeEnter.append('text').call(this.drawNodeText);

    // Transition nodes to their new position.
    const nodeUpdate = node.merge(nodeEnter);

    const nodeUpdateTransition = nodeUpdate
      .transition()
      .duration(duration)
      .attr('transform', this.getNodeTransform)
      .attr('fill-opacity', 1)
      .attr('stroke-opacity', 1);

    nodeUpdateTransition
      .selectAll<SVGCircleElement, d3.HierarchyPointNode<Datum>>('circle')
      .call(this.drawNodeCircle);

    nodeUpdate
      .selectAll<SVGTextElement, d3.HierarchyPointNode<Datum>>('text')
      .call(this.drawNodeText);

    // Transition exiting nodes to the parent's new position.
    node
      .exit()
      .transition()
      .duration(duration)
      .attr('transform', (d: any) => {
        const src = source ? source : d.data.source;
        const o = { x: src.x!, y: src.y! };
        return this.getNodeTransform(o);
      })
      .attr('fill-opacity', 0)
      .attr('stroke-opacity', 0)
      .remove();

    const link = this.linkGroup
      .selectAll<SVGPathElement, d3.HierarchyPointLink<Datum>>('path')
      .data(links, d => `${d.target.data.id}`);

    // Enter any new links at the parent's previous position.
    const linkEnter = link
      .enter()
      .append('path')
      .attr('d', d => {
        const src = source ? source : d.target.data.source;
        const o = { x: src.data.x0!, y: src.data.y0! };
        const linkGenerator = this.getLinkGenerator();
        return linkGenerator({ source: o, target: o });
      });

    // Transition links to their new position.
    const linkUpdate = link
      .merge(linkEnter)
      .transition()
      .duration(duration)
      .attr('d', this.getLinkGenerator());

    // Transition exiting nodes to the parent's new position.
    link
      .exit()
      .transition()
      .duration(duration)
      .attr('d', (d: any) => {
        const src = source ? source : d.target.data.source;
        const o = { x: src.x!, y: src.y! };
        const linkGenerator = this.getLinkGenerator();
        return linkGenerator({ source: o, target: o });
      })
      .remove();

    return Promise.all([nodeUpdateTransition.end(), linkUpdate.end()]).then(
      () => {
        this.root.each(this.saveCurrentPosition);
      }
    );
  }

  private saveCurrentPosition(node: d3.HierarchyPointNode<Datum>) {
    node.data.x0 = node.x;
    node.data.y0 = node.y;
  }

  private drawNodeCircle(circle) {
    return circle
      .attr('fill', d => d.data.rgb)
      .attr('stroke', this.getNodeStroke)
      .attr('r', this.getNodeRadius);
  }

  private drawNodeText(text) {
    text
      .attr('dy', '0.40em')
      .attr('x', d => {
        if (this.isForEdit(d)) {
          return 0;
        } else {
          return d.x < Math.PI === !d.children ? 10 : -10;
        }
      })
      .attr('text-anchor', d => {
        if (this.isForEdit(d)) {
          return 'middle';
        } else {
          return d.x < Math.PI === !d.children ? 'start' : 'end';
        }
      })
      .attr('transform', this.getNodeTextTransform)
      .attr('font-size', this.getNodeTextFontSize)
      .text(this.getNodeText);
  }

  private async onNodeClicked(node: d3.HierarchyPointNode<Datum>) {
    d3.event.stopPropagation();

    if (this.isForAlter(node)) {
      // [Alter] - a child of the selected component is clicked.

      const lastInteractionRecord = this.interactionRecords[
        this.interactionRecords.length - 1
      ];
      lastInteractionRecord.interactionLabel = this.isForEnglishPaper
        ? this.interactionLabelsDict.english.alter
        : this.interactionLabelsDict.korean.alter;
      lastInteractionRecord.targetNodes.push(node);
      this.initialComponentNodes = _.filter(
        this.initialComponentNodes,
        componentNode => {
          return (
            lastInteractionRecord.sourceNode.data.id !== componentNode.data.id
          );
        }
      );

      this.removeComponent(node.parent!);
      this.addComponent(node);
      this.sortComponents();

      this.clickedNode = undefined;

      await this.onCollapseAfterChanged();
    } else if (this.isForSplit(node)) {
      // [Split] - the selected component is clicked again.

      const lastInteractionRecord = this.interactionRecords[
        this.interactionRecords.length - 1
      ];
      lastInteractionRecord.interactionLabel = this.isForEnglishPaper
        ? this.interactionLabelsDict.english.split
        : this.interactionLabelsDict.korean.split;
      lastInteractionRecord.targetNodes = [
        node.children![0],
        node.children![1]
      ];
      this.initialComponentNodes = _.filter(
        this.initialComponentNodes,
        componentNode => {
          return (
            lastInteractionRecord.sourceNode.data.id !== componentNode.data.id
          );
        }
      );

      this.removeComponent(node);
      this.addComponents(...node.children!);
      this.sortComponents();

      this.clickedNode = undefined;

      await this.onCollapseAfterChanged();
    } else if (this.isForMerge(node)) {
      // [Merge] - the sibling of the selected component is clicked.

      const lastInteractionRecord = this.interactionRecords[
        this.interactionRecords.length - 1
      ];
      lastInteractionRecord.interactionLabel = this.isForEnglishPaper
        ? this.interactionLabelsDict.english.merge
        : this.interactionLabelsDict.korean.merge;
      lastInteractionRecord.targetNodes.push(node.parent!);
      this.initialComponentNodes = _.filter(
        this.initialComponentNodes,
        componentNode => {
          return (
            lastInteractionRecord.sourceNode.data.id !== componentNode.data.id
          );
        }
      );

      this.removeComponents(...node.parent!.children!);
      this.addComponent(node.parent!);
      this.sortComponents();

      this.clickedNode = undefined;

      await this.onCollapseAfterChanged();
    } else if (this.isForRevert(node)) {
      // [Revert] - the parent of the selected component is clicked.

      const lastInteractionRecord = this.interactionRecords[
        this.interactionRecords.length - 1
      ];
      lastInteractionRecord.interactionLabel = this.isForEnglishPaper
        ? this.interactionLabelsDict.english.revert
        : this.interactionLabelsDict.korean.revert;
      lastInteractionRecord.targetNodes.push(node);
      this.initialComponentNodes = _.filter(
        this.initialComponentNodes,
        componentNode => {
          return (
            lastInteractionRecord.sourceNode.data.id !== componentNode.data.id
          );
        }
      );

      this.removeComponent(this.clickedNode!);
      this.addComponent(node);
      this.sortComponents();

      this.clickedNode = undefined;

      await this.onCollapseAfterChanged();
    } else if (this.isComponent(node)) {
      this.clickedNode = node;

      this.interactionRecords.push({
        sourceNode: node,
        targetNodes: [],
        interactionLabel: null
      });

      await this.drawHierarchy(125, node.parent!);
    } else if (this.isCollapsible(node)) {
      if (this.isFolded(node)) {
        this.unfoldNode(node);
      } else {
        this.foldNode(node);
      }

      this.updateLayout(); // Because the collapse is updated, re-layout.
      await this.draw(250, node); // draw
      this.updateViewBox();
    }
  }

  private isForEdit(node): boolean {
    const fs = [
      this.isForAlter,
      this.isForSplit,
      this.isForMerge,
      this.isForRevert
    ];
    return fs.some(f => f(node));
  }

  private isForAlter(node: d3.HierarchyPointNode<Datum>) {
    return node.parent && this.isClickedNode(node.parent);
  }

  private isForSplit(node: d3.HierarchyPointNode<Datum>) {
    return this.isComponent(node) && this.isClickedNode(node) && node.children;
  }

  private isForMerge(node: d3.HierarchyPointNode<Datum>) {
    return (
      this.isComponent(node) &&
      node.parent &&
      node.parent.children!.some(d => d !== node && this.isClickedNode(d))
    );
  }

  private isForRevert(node: d3.HierarchyPointNode<Datum>) {
    return (
      node.children &&
      node.children.some(c => this.isClickedNode(c)) && // one of the children is clicked
      !node.children.every(this.hasComponent)
    ); // not all of the children are component
  }

  private isCollapsible(node: d3.HierarchyPointNode<Datum>) {
    const foldable = node.children && !node.children.some(this.hasComponent);
    const unfoldable = !node.children && node.data._children;
    return foldable || unfoldable;
  }

  private drawHintingArea() {
    const links = this.linkGroup.selectAll<
      SVGPathElement,
      d3.HierarchyPointLink<Datum>
    >('path');
    return this.areaGroupRenderer.render(links, this.horizontalLayout);
  }

  private getSvgElement(): SVGSVGElement {
    const svgElement = this.svg.node();
    if (svgElement) {
      return svgElement;
    }
    throw new Error('svgElement should be defined');
  }

  private getParentElement(): HTMLElement {
    const parentElement = this.getSvgElement().parentElement;
    if (parentElement) {
      return parentElement;
    }
    throw new Error('parentElement should be defined');
  }

  private getRect(pl = 16, pt = 16, pr = 16, pb = 16): DOMRect {
    const rect: DOMRect = this.getParentElement().getBoundingClientRect() as DOMRect;
    rect.x += pl;
    rect.y += pt;
    rect.width -= pl + pr;
    rect.height -= pt + pb;
    return rect;
  }

  private getLayout() {
    const { width, height } = this.getRect();
    const extent = math.min(width, height);
    const radius = extent / 2;
    const radialSize: [number, number] = [2 * Math.PI, radius];

    if (this.verticalLayout === 'topdown') {
      if (this.horizontalLayout === 'radialTidy') {
        return d3
          .tree<Datum>()
          .size(radialSize)
          .separation((a, b) => (a.parent === b.parent ? 1 : 2) / a.depth);
      } else if (this.horizontalLayout === 'linearTidy') {
        return (root: d3.HierarchyNode<Datum>) => {
          root.data.dx = 10;
          root.data.dy = extent / (root.height + 1);
          // return d3.tree<Datum>().nodeSize([root.data.dx, root.data.dy])(root);
          return d3.tree<Datum>().size([width, height])(root);
        };
      }
    } else if (this.verticalLayout === 'bottomup') {
      if (this.horizontalLayout === 'radialTidy') {
        return d3.cluster<Datum>().size(radialSize);
      } else if (this.horizontalLayout === 'linearTidy') {
        return (root: d3.HierarchyNode<Datum>) => {
          root.data.dx = 10;
          root.data.dy = extent / (root.height + 1);
          // root.data.dy = 40;
          // return d3.cluster<Datum>().nodeSize([root.data.dx, root.data.dy])(root);
          return d3.cluster<Datum>().size([width, height])(root);
        };
      }
    }

    throw new Error('Unsupported verticalLayout');
  }

  private autoBox() {
    const rect = this.getRect();
    let { x, y } = rect;
    const { width, height } = rect;
    if (this.horizontalLayout === 'radialTidy') {
      x = -width / 2;
      y = -height / 2;
    } else {
      x = 0;
      y = 0;
    }
    return `${[x, y, width, height]}`;
  }

  private getNodeStroke(node: d3.HierarchyPointNode<Datum>) {
    if (this.isClickedNode(node)) {
      return 'green';
    }
    if (this.isForEdit(node)) {
      return 'red';
    }
    if (this.isComponent(node)) {
      return 'black';
    }
    return 'none';
  }

  private getNodeRadius(node: d3.HierarchyPointNode<Datum>) {
    const maxDepth = this.root.descendants().slice(-1)[0].depth;
    const baseSize = Math.max(5, 8 - maxDepth + 4);
    if (this.isForEdit(node)) {
      return 2.5 * baseSize;
    }
    if (this.isComponent(node)) {
      return 2.0 * baseSize;
    }
    return baseSize;
  }

  private getNodeTransform(d: { x: number; y: number }) {
    if (this.horizontalLayout === 'radialTidy') {
      return `
        rotate(${(d.x * 180) / Math.PI - 90})
        translate(${d.y},0)
      `;
    } else {
      return `translate(${d.x},${d.y})`;
    }
  }

  private getNodeTextTransform(d: d3.HierarchyPointNode<Datum>): string {
    const nodeRadius = this.getNodeRadius(d);
    let transformAttribute: string = '';
    if (this.horizontalLayout === 'radialTidy') {
      if (this.isForEdit(d)) {
        transformAttribute = `rotate(${-((d.x * 180) / Math.PI - 90)})`;
      } else {
        transformAttribute = d.x >= Math.PI ? 'rotate(180)' : '';
      }
      transformAttribute += this.isForEdit(d)
        ? ` translate(0, ${nodeRadius + 8})`
        : '';
    } else {
      // linearTidy
      transformAttribute = 'translate(18, 14)';
    }

    return transformAttribute;
  }

  private getNodeTextFontSize(d: d3.HierarchyPointNode<Datum>): number {
    const maxDepth = this.root.descendants().slice(-1)[0].depth;
    const baseSize = Math.max(13, 8 - maxDepth + 4);
    return baseSize;
  }

  private getLinkGenerator() {
    if (this.horizontalLayout === 'radialTidy') {
      return d3
        .linkRadial<any, any>()
        .angle(d => d.x)
        .radius(d => d.y);
    } else {
      return d3
        .linkVertical<any, any>()
        .x(d => d.x)
        .y(d => d.y);
    }
  }

  private getNodeText(node: d3.HierarchyPointNode<Datum>) {
    let nodeText: string = '';
    if (!node.children) {
      if (node.data.numLeaves > 1) {
        nodeText = `+${node.data.numLeaves}`;
      }
    }
    if (this.isForAlter(node)) {
      nodeText = this.isForEnglishPaper
        ? this.interactionLabelsDict.english.alter
        : this.interactionLabelsDict.korean.alter;
    }
    if (this.isForSplit(node)) {
      nodeText = this.isForEnglishPaper
        ? this.interactionLabelsDict.english.split
        : this.interactionLabelsDict.korean.split;
    }
    if (this.isForMerge(node)) {
      nodeText = this.isForEnglishPaper
        ? this.interactionLabelsDict.english.merge
        : this.interactionLabelsDict.korean.merge;
    }
    if (this.isForRevert(node)) {
      nodeText = this.isForEnglishPaper
        ? this.interactionLabelsDict.english.revert
        : this.interactionLabelsDict.korean.revert;
    }
    return nodeText;
  }

  private getWard(node: d3.HierarchyPointNode<Datum>) {
    return node.data.ward;
  }

  private compareWard(
    a: d3.HierarchyPointNode<Datum>,
    b: d3.HierarchyPointNode<Datum>
  ) {
    return this.getWard(a) - this.getWard(b);
  }

  private sortComponents(
    compareFn?: (
      a: d3.HierarchyPointNode<Datum>,
      b: d3.HierarchyPointNode<Datum>
    ) => number
  ) {
    if (!compareFn) {
      compareFn = this.compareWard;
    }
    this.componentNodes.sort(compareFn);
  }

  private addComponent(component: d3.HierarchyPointNode<Datum>) {
    const start = this.componentNodes.indexOf(component);
    if (start >= 0) {
      throw new Error('The component already exists.');
    }
    this.componentNodes.push(component);
  }

  private addComponents(...components: Array<d3.HierarchyPointNode<Datum>>) {
    if (!components.length) {
      throw new Error('No components are provided.');
    }
    components.forEach(this.addComponent);
  }

  private removeComponent(component: d3.HierarchyPointNode<Datum>) {
    const start = this.componentNodes.indexOf(component);
    if (start < 0) {
      throw new Error('There is no such component.');
    }
    this.componentNodes.splice(start, 1);
    if (this.clickedNode === component) {
      this.clickedNode = undefined;
    }
  }

  private removeComponents(...components: Array<d3.HierarchyPointNode<Datum>>) {
    if (!components.length) {
      throw new Error('No components are provided.');
    }
    components.forEach(this.removeComponent);
  }

  private joinComponents(
    newComponentNodes: Array<d3.HierarchyPointNode<Datum>>,
    sort: boolean = true
  ) {
    if (this.componentNodes.length === newComponentNodes.length) {
      const a = this.componentNodes.map(n => n.data.id!).sort();
      const b = newComponentNodes.map(n => n.data.id!).sort();
      if (a.join() === b.join()) {
        console.warn(
          'joinComponents:',
          'Trying to join two same arrays. Ignored.'
        );
        return;
      }
    }
    this.removeComponents(
      ...this.componentNodes.filter(n => !newComponentNodes.includes(n))
    );
    this.addComponents(
      ...newComponentNodes.filter(n => !this.componentNodes.includes(n))
    );
    if (sort) {
      this.sortComponents();
    }
  }

  private getComponentsSortedForDisplay() {
    const getInd = (d: Datum) => (d.sortKey ? d.sortKey : d.id);
    return Array<d3.HierarchyPointNode<Datum>>()
      .concat(this.componentNodes)
      .sort((a, b) => getInd(a.data) - getInd(b.data));
  }

  get colorCodes() {
    return this.getComponentsSortedForDisplay().map(c =>
      (c.data.rgb as d3.RGBColor).hex()
    );
  }

  private copyTexts(texts: string[]) {
    this.copyToClipboard(JSON.stringify(texts));
    alert(`copied! ${JSON.stringify(texts)}`);
  }
  private copyToClipboard(text: string) {
    const textAreaElement = document.createElement('textarea');
    document.body.appendChild(textAreaElement);
    textAreaElement.value = text;
    textAreaElement.select();
    document.execCommand('copy');
    document.body.removeChild(textAreaElement);
  }
}
